(() => {
  if (customElements.get('be-infinite-products')) return;
  class InfiniteProducts extends HTMLElement {
    connectedCallback() {
      this.grid = this.parentElement.querySelector('ul.product-grid');
      this.link = this.querySelector('[data-next-link]');
      this.status = this.querySelector('[role="status"]');
      if (!this.grid || !this.link || !('IntersectionObserver' in window)) return;
      this.stopped = false;
      this.seen = new Set();
      this.cacheKey = `be-list:${location.pathname}${location.search}:${this.dataset.section}`;
      // Native bfcache keeps the DOM and scroll position. Restore the loaded list
      // only when Back required a fresh document instead.
      try {
        if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
          const saved = JSON.parse(sessionStorage.getItem(this.cacheKey) || 'null');
          if (saved && Date.now() - saved.time < 1800000) {
            this.grid.innerHTML = saved.html;
            this.dataset.next = saved.next;
            if (!window.beScrollRestoration) requestAnimationFrame(() => window.scrollTo(0, saved.y));
          }
        }
      } catch (_) { /* Storage disabled: native browser restoration remains. */ }
      this.save = () => {
        try {
          const snapshot = this.grid.cloneNode(true);
          // App-generated buttons contain listeners that cannot be serialized.
          // Keep their data placeholders so the existing Wishlist Hero initializer
          // can bind fresh buttons after restoration.
          snapshot.querySelectorAll('.be-wishlist').forEach(el => el.replaceChildren());
          sessionStorage.setItem(this.cacheKey, JSON.stringify({
            html: snapshot.innerHTML, next: this.dataset.next, y: window.scrollY, time: Date.now()
          }));
        } catch (_) { /* Quota/private mode must not block browsing. */ }
      };
      window.addEventListener('pagehide', this.save);
      this.link.hidden = true;
      this.link.onclick = event => { event.preventDefault(); this.load(); };
      this.cancel = () => { this.stopped = true; this.controller?.abort(); this.observer.disconnect(); };
      document.addEventListener('be:filters-start', this.cancel);
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.load();
      }, { rootMargin: '0px 0px 160px 0px' });
      if (this.dataset.next) this.observer.observe(this);
      else this.hidden = true;
    }
    disconnectedCallback() {
      this.cancel?.();
      document.removeEventListener('be:filters-start', this.cancel);
      window.removeEventListener('pagehide', this.save);
    }
    async load() {
      const next = this.dataset.next;
      if (this.busy || this.stopped || !next || this.seen.has(next)) return;
      this.busy = true;
      this.status.textContent = '読み込み中…';
      this.status.hidden = false;
      this.link.hidden = true;
      this.controller = new AbortController();
      try {
        const url = new URL(next, location.href);
        if (url.origin !== location.origin) throw new Error('Invalid pagination origin');
        url.searchParams.set('section_id', this.dataset.section);
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Pagination request failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const pager = [...doc.querySelectorAll('be-infinite-products')].find(el => el.dataset.section === this.dataset.section);
        // Last pages still render the pager because paginate.pages remains > 1.
        const grid = pager?.parentElement.querySelector('ul.product-grid');
        if (!grid) throw new Error('Missing product grid');
        if (!this.isConnected || this.stopped) return;
        const identity = el => el.querySelector('a[href*="/products/"]')?.getAttribute('href')?.split('?')[0];
        const existing = new Set([...this.grid.children].map(identity).filter(Boolean));
        for (const item of [...grid.children]) {
          const id = identity(item);
          if (id && existing.has(id)) continue;
          if (id) existing.add(id);
          item.classList.add('scroll-trigger--cancel');
          this.grid.append(item);
        }
        this.seen.add(next);
        this.dataset.next = pager.dataset.next || '';
        this.link.href = this.dataset.next || '#';
        this.status.hidden = true;
        this.dispatchEvent(new CustomEvent('be:products-appended', { bubbles: true }));
        if (!this.dataset.next || this.seen.has(this.dataset.next)) {
          this.observer.disconnect();
          this.hidden = true;
        } else {
          this.observer.unobserve(this);
          this.observer.observe(this);
        }
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected && !this.stopped) {
          this.observer.disconnect();
          this.status.textContent = '読み込めませんでした。もう一度お試しください。';
          this.link.hidden = false;
        }
      } finally { this.busy = false; }
    }
  }
  customElements.define('be-infinite-products', InfiniteProducts);
})();
